import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { STORAGE_KEYS } from '../constants.ts';
import api from '../utils/api.ts';
import './AdminLogin.css';

function AdminLogin() {
  console.log('[AdminLogin] Component rendering');
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [alias, setAlias] = useState('');
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' }>({ text: '', type: 'error' });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('[AdminLogin] useEffect triggered - checking auth');
    let isMounted = true;
    const checkAuth = async () => {
      const result = await api.verifyToken();
      console.log('[AdminLogin] Token verification result:', result);
      if (result.success && isMounted) {
        console.log('[AdminLogin] Token valid, navigating to /main');
        navigate('/main', { replace: true });
      } else {
        console.log('[AdminLogin] Token invalid or not found');
      }
    };
    checkAuth();
    return () => { isMounted = false; };
  }, []);

  const handleGoogleCallback = useCallback(async (response: any) => {
    console.log('[AdminLogin] Google callback triggered');
    setIsLoading(true);
    try {
      const payload = JSON.parse(atob(response.credential.split('.')[1]));
      console.log('[AdminLogin] Google payload:', { email: payload.email, name: payload.name });
      
      const result = await api.googleAuth(
        response.credential,
        payload.email,
        payload.name,
        payload.sub
      );

      console.log('[AdminLogin] Google auth result:', result);
      if (result.success) {
        console.log('[AdminLogin] Google login successful, redirecting to /main');
        setMessage({ text: 'Google login successful! Redirecting...', type: 'success' });
        navigate('/main', { replace: true });
      } else {
        console.error('[AdminLogin] Google auth failed:', result.error);
        setMessage({ text: result.error || 'Google authentication failed', type: 'error' });
      }
    } catch (error) {
      console.error('[AdminLogin] Google callback error:', error);
      setMessage({ text: 'Failed to process Google authentication', type: 'error' });
    }
    setIsLoading(false);
  }, [navigate]);

  useEffect(() => {
    console.log('[AdminLogin] Google button setup effect triggered');
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    if (!clientId || clientId === 'your-google-client-id-here.apps.googleusercontent.com') {
      console.error('Google Client ID not configured');
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      console.log('[AdminLogin] Google script loaded');
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleGoogleCallback,
          ux_mode: 'popup',
        });
        
        const buttonDiv = document.getElementById('google-signin-button');
        if (buttonDiv) {
          console.log('[AdminLogin] Rendering Google button');
          window.google.accounts.id.renderButton(
            buttonDiv,
            { 
              theme: 'outline',
              size: 'large',
              width: buttonDiv.offsetWidth,
              text: 'continue_with'
            }
          );
        }
      }
    };

    return () => {
      console.log('[AdminLogin] Cleaning up Google script');
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [handleGoogleCallback]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };



  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage({ text: '', type: 'error' });
    setIsLoading(true);

    if (!validateEmail(email)) {
      setMessage({ text: 'Please enter a valid email address', type: 'error' });
      setIsLoading(false);
      return;
    }

    const result = await api.login(email, password);

    if (result.success) {
      setMessage({ text: 'Login successful! Redirecting...', type: 'success' });
      navigate('/main', { replace: true });
    } else {
      setMessage({ text: result.error || 'Invalid credentials', type: 'error' });
    }
    setIsLoading(false);
  };

  const handleSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage({ text: '', type: 'error' });
    setIsLoading(true);

    try {
      if (!validateEmail(email)) {
        setMessage({ text: 'Please enter a valid email address', type: 'error' });
        setIsLoading(false);
        return;
      }

      if (password !== passwordConfirm) {
        setMessage({ text: 'Passwords do not match', type: 'error' });
        setIsLoading(false);
        return;
      }

      if (alias.length < 2) {
        setMessage({ text: 'Alias must be at least 2 characters long', type: 'error' });
        setIsLoading(false);
        return;
      }

      const result = await api.signup(email, password, passwordConfirm, alias);

      if (result.success) {
        setMessage({ text: 'Signup successful! Logging you in...', type: 'success' });
        navigate('/main', { replace: true });
      } else {
        setMessage({ text: result.error || 'Signup failed', type: 'error' });
      }
    } catch (error) {
      console.error('Signup error:', error);
      setMessage({ text: 'An unexpected error occurred', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setMessage({ text: '', type: 'error' });
    setEmail('');
    setPassword('');
    setPasswordConfirm('');
    setAlias('');
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>🎲 {mode === 'login' ? 'Login' : 'Sign Up'}</h1>
        
        <form onSubmit={mode === 'login' ? handleLogin : handleSignup}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            disabled={isLoading}
          />
          
          {mode === 'signup' && (
            <input
              type="text"
              placeholder="In-game Alias"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              required
              disabled={isLoading}
              minLength={2}
              maxLength={50}
            />
          )}
          
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            disabled={isLoading}
          />
          
          {mode === 'signup' && (
            <>
              <input
                type="password"
                placeholder="Confirm Password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
                autoComplete="new-password"
                disabled={isLoading}
              />
            </>
          )}
          
          <button type="submit" disabled={isLoading}>
            {isLoading ? 'Processing...' : mode === 'login' ? 'Login' : 'Sign Up'}
          </button>
        </form>

        <div className="divider">
          <span>OR</span>
        </div>

        <div id="google-signin-button" style={{ width: '100%' }}></div>

        <div className="switch-mode">
          <button type="button" onClick={switchMode} disabled={isLoading}>
            {mode === 'login' 
              ? "Don't have an account? Sign up" 
              : 'Already have an account? Login'}
          </button>
        </div>

        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
}

declare global {
  interface Window {
    google?: any;
  }
}

export default AdminLogin;
